import { norm, parseCsvLines } from './format';
import {
  type CsvImportError,
  type FieldError,
  type SkuRowPayload,
  type SkuStatus,
  WAREHOUSE_UOMS,
  type WarehouseSku,
} from './types';

/**
 * The SKU master as ops keep it (their master_sku sheet): one definition per
 * property — its sheet header (also the form label: ops know the properties
 * by these names), JSON key, type and limit — shared by the stepper form, the
 * detail panel and the CSV import. Groups are the form steps.
 */

type TextKey =
  | 'itemName'
  | 'brand'
  | 'productCategory'
  | 'productType'
  | 'productVariant'
  | 'color'
  | 'size'
  | 'material'
  | 'storageType'
  | 'barcode'
  | 'imageUrl';
type DecimalKey = 'weightKg' | 'heightCm' | 'widthCm' | 'lengthCm';
type IntKey =
  | 'pcsPerInnerBox'
  | 'innerBoxPerCarton'
  | 'pcsPerCarton'
  | 'cartonPerPallet'
  | 'pcsPerPallet';
export type SkuBoolKey =
  | 'batchTracking'
  | 'expiryTracking'
  | 'serialNumberTracking'
  | 'imeiTracking'
  | 'qcRequired'
  | 'fragile'
  | 'stackable';
type StringKey = 'sku' | 'uomName' | 'status' | TextKey | DecimalKey | IntKey;
export type SkuPropKey = StringKey | SkuBoolKey;

export type SkuPropKind =
  | 'code'
  | 'text'
  | 'decimal'
  | 'int'
  | 'bool'
  | 'uom'
  | 'status'
  /** A stored image URL (uploaded by the form, not typed; not in the CSV). */
  | 'image';

export interface SkuPropDef {
  key: SkuPropKey;
  /** Sheet header = form label. */
  label: string;
  kind: SkuPropKind;
  /** Largest number accepted (decimal / int). */
  maxValue?: number;
  required?: boolean;
  /** Indonesian explanation (toggles, codes). */
  hint?: string;
}

const text = (key: TextKey, label: string, required = false): SkuPropDef => ({
  key,
  label,
  kind: 'text',
  required,
});
const flag = (key: SkuBoolKey, label: string): SkuPropDef => ({
  key,
  label,
  kind: 'bool',
});

/** The server's upper limits (numeric(…) / int4 columns). */
const CM_MAX = 99999.9;
const KG_MAX = 99999.999;
const INT_MAX = 2147483647;

/** Every property, in the sheet's column order. */
export const SKU_PROPS: SkuPropDef[] = [
  {
    key: 'sku',
    label: 'SKU Code',
    kind: 'code',
    required: true,
  },
  text('itemName', 'SKU Name', true),
  text('brand', 'Client / Brand'),
  text('productCategory', 'Product Category'),
  text('productType', 'Product Type'),
  text('productVariant', 'Product Variant'),
  text('color', 'Color'),
  text('size', 'Size'),
  text('material', 'Material'),
  { key: 'uomName', label: 'UOM', kind: 'uom', required: true },
  {
    key: 'weightKg',
    label: 'Weight / PCS (kg)',
    kind: 'decimal',
    maxValue: KG_MAX,
  },
  { key: 'heightCm', label: 'Height (cm)', kind: 'decimal', maxValue: CM_MAX },
  { key: 'widthCm', label: 'Width (cm)', kind: 'decimal', maxValue: CM_MAX },
  { key: 'lengthCm', label: 'Length (cm)', kind: 'decimal', maxValue: CM_MAX },
  {
    key: 'storageType',
    label: 'Storage Type',
    kind: 'text',
  },
  flag('batchTracking', 'Batch Tracking'),
  flag('expiryTracking', 'Expiry Tracking'),
  flag('serialNumberTracking', 'Serial Number Tracking'),
  flag('imeiTracking', 'IMEI Tracking'),
  flag('qcRequired', 'QC Required'),
  flag('fragile', 'Fragile'),
  flag('stackable', 'Stackable'),
  {
    key: 'pcsPerInnerBox',
    label: 'PCS / Inner Box',
    kind: 'int',
    maxValue: INT_MAX,
  },
  {
    key: 'innerBoxPerCarton',
    label: 'Inner Box / Carton',
    kind: 'int',
    maxValue: INT_MAX,
  },
  {
    key: 'pcsPerCarton',
    label: 'PCS / Carton',
    kind: 'int',
    maxValue: INT_MAX,
  },
  {
    key: 'cartonPerPallet',
    label: 'Carton / Pallet',
    kind: 'int',
    maxValue: INT_MAX,
  },
  {
    key: 'pcsPerPallet',
    label: 'PCS / Pallet',
    kind: 'int',
    maxValue: INT_MAX,
  },
  text('barcode', 'Barcode / EAN'),
  {
    key: 'imageUrl',
    label: 'Sample image',
    kind: 'image',
  },
  { key: 'status', label: 'Status', kind: 'status' },
];

export const SKU_PROP: Record<SkuPropKey, SkuPropDef> = Object.fromEntries(
  SKU_PROPS.map((p) => [p.key, p])
) as Record<SkuPropKey, SkuPropDef>;

export interface SkuGroup {
  id: number;
  /** Indonesian group heading (= wizard step). */
  label: string;
  keys: SkuPropKey[];
}

/** Form steps / detail sections; Review is the step after the last group. */
export const SKU_GROUPS: SkuGroup[] = [
  {
    id: 1,
    label: 'Identitas',
    keys: ['sku', 'itemName', 'brand', 'barcode', 'status', 'imageUrl'],
  },
  {
    id: 2,
    label: 'Klasifikasi',
    keys: [
      'productCategory',
      'productType',
      'productVariant',
      'color',
      'size',
      'material',
    ],
  },
  {
    id: 3,
    label: 'Ukuran & Kemasan',
    keys: [
      'uomName',
      'weightKg',
      'lengthCm',
      'widthCm',
      'heightCm',
      'pcsPerInnerBox',
      'innerBoxPerCarton',
      'pcsPerCarton',
      'cartonPerPallet',
      'pcsPerPallet',
    ],
  },
  {
    id: 4,
    label: 'Handling & Tracking',
    keys: [
      'storageType',
      'qcRequired',
      'fragile',
      'stackable',
      'batchTracking',
      'expiryTracking',
      'serialNumberTracking',
      'imeiTracking',
    ],
  },
];

export const SKU_STEP_OF = Object.fromEntries(
  SKU_GROUPS.flatMap((g) => g.keys.map((k) => [k, g.id]))
) as Record<SkuPropKey, number>;

export const SKU_STATUS_LABEL: Record<SkuStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Non Active',
};

/* --------------------------------- draft --------------------------------- */

/** Form state: text for every input, booleans for the toggles. */
export type SkuDraft = Record<StringKey, string> & Record<SkuBoolKey, boolean>;

export const emptySkuDraft = (sku = ''): SkuDraft => {
  const d = {} as Record<string, string | boolean>;
  SKU_PROPS.forEach((p) => {
    d[p.key] = p.kind === 'bool' ? false : '';
  });
  d.sku = sku;
  d.uomName = 'pcs';
  d.status = 'ACTIVE';
  return d as SkuDraft;
};

const str = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v);

export const draftFromSku = (s: WarehouseSku): SkuDraft => {
  const d = emptySkuDraft();
  const rec = s as unknown as Record<string, unknown>;
  SKU_PROPS.forEach((p) => {
    const v = rec[p.key];
    if (p.kind === 'bool') (d as Record<string, unknown>)[p.key] = v === true;
    else if (p.key === 'status')
      d.status = v === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE';
    else (d as Record<string, unknown>)[p.key] = str(v);
  });
  return d;
};

/**
 * Validate a draft into the API row. Errors use the JSON field names, so
 * local and server (422) errors map onto the form the same way.
 */
export const readSkuDraft = (
  d: SkuDraft
): { value: SkuRowPayload | null; errors: FieldError[] } => {
  const errors: FieldError[] = [];
  const out: Record<string, unknown> = {};
  const fail = (key: SkuPropKey, message: string) =>
    errors.push({ field: key, message });

  SKU_PROPS.forEach((p) => {
    if (p.kind === 'bool') {
      out[p.key] = (d as Record<string, unknown>)[p.key] === true;
      return;
    }
    const raw = str((d as Record<string, unknown>)[p.key]).trim();
    switch (p.kind) {
      case 'code': {
        const v = raw.toUpperCase();
        if (!v) fail(p.key, `${p.label} wajib diisi.`);
        out[p.key] = v;
        break;
      }
      case 'text':
      case 'image':
        if (p.required && !raw) fail(p.key, `${p.label} wajib diisi.`);
        out[p.key] = p.required ? raw : raw || null;
        break;
      case 'uom': {
        const v = raw.toLowerCase();
        if (!(WAREHOUSE_UOMS as readonly string[]).includes(v))
          fail(
            p.key,
            `${p.label} harus salah satu: ${WAREHOUSE_UOMS.join(', ')}.`
          );
        out[p.key] = v;
        break;
      }
      case 'status':
        if (raw !== 'ACTIVE' && raw !== 'INACTIVE')
          fail(p.key, `${p.label} harus Active atau Non Active.`);
        out[p.key] = raw;
        break;
      case 'decimal': {
        if (!raw) {
          out[p.key] = null;
          break;
        }
        const n = Number(raw.replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0)
          fail(p.key, `${p.label} harus angka > 0.`);
        else if (p.maxValue !== undefined && n > p.maxValue)
          fail(p.key, `${p.label} maksimal ${p.maxValue}.`);
        out[p.key] = n;
        break;
      }
      case 'int': {
        if (!raw) {
          out[p.key] = null;
          break;
        }
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 1)
          fail(p.key, `${p.label} harus bilangan bulat ≥ 1.`);
        else if (p.maxValue !== undefined && n > p.maxValue)
          fail(p.key, `${p.label} maksimal ${p.maxValue}.`);
        out[p.key] = n;
        break;
      }
      default:
        break;
    }
  });
  return {
    value: errors.length ? null : (out as unknown as SkuRowPayload),
    errors,
  };
};

/** CBM per unit (m³) from L × W × H in cm; null until all three are set. */
export const cbmOf = (v: {
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
}): number | null =>
  v.lengthCm && v.widthCm && v.heightCm
    ? (v.lengthCm * v.widthCm * v.heightCm) / 1_000_000
    : null;

const intOf = (s: string | number | null | undefined): number | null => {
  const n = Number(s);
  return s !== '' &&
    s !== null &&
    s !== undefined &&
    Number.isInteger(n) &&
    n > 0
    ? n
    : null;
};

/**
 * Soft packaging checks (never block: the sheet's own sample is "off"):
 * PCS/Carton vs PCS/Inner Box × Inner Box/Carton, PCS/Pallet vs
 * PCS/Carton × Carton/Pallet.
 */
export const packagingHints = (v: {
  pcsPerInnerBox?: string | number | null;
  innerBoxPerCarton?: string | number | null;
  pcsPerCarton?: string | number | null;
  cartonPerPallet?: string | number | null;
  pcsPerPallet?: string | number | null;
}): string[] => {
  const inner = intOf(v.pcsPerInnerBox);
  const boxes = intOf(v.innerBoxPerCarton);
  const carton = intOf(v.pcsPerCarton);
  const cartons = intOf(v.cartonPerPallet);
  const pallet = intOf(v.pcsPerPallet);
  const out: string[] = [];
  if (inner && boxes && carton && inner * boxes !== carton)
    out.push(
      `PCS / Carton ${carton} ≠ PCS / Inner Box × Inner Box / Carton (${inner} × ${boxes} = ${inner * boxes}).`
    );
  if (carton && cartons && pallet && carton * cartons !== pallet)
    out.push(
      `PCS / Pallet ${pallet} ≠ PCS / Carton × Carton / Pallet (${carton} × ${cartons} = ${carton * cartons}).`
    );
  return out;
};

/* ---------------------------------- CSV ---------------------------------- */

/** The sheet's columns: every property but the image (uploaded, never typed). */
const CSV_PROPS = SKU_PROPS.filter((p) => p.kind !== 'image');

/** Template header — exactly the sheet's columns, in order. */
export const SKU_CSV_HEADERS = CSV_PROPS.map((p) => p.label);

const HEADER_KEY = new Map<string, SkuPropKey>(
  CSV_PROPS.flatMap((p) => [
    [norm(p.label), p.key],
    [norm(p.key), p.key],
  ])
);

const YES = new Set(['yes', 'y', 'ya', 'true', '1']);
const NO = new Set(['no', 'n', 'tidak', 'false', '0', '']);
const ACTIVE = new Set(['active', 'aktif', '']);
const INACTIVE = new Set([
  'nonactive',
  'non-active',
  'inactive',
  'nonaktif',
  'non-aktif',
]);

export interface ParsedSkuRow {
  /** 1-based row number in the file (what ops see in the sheet). */
  line: number;
  sku: string;
  itemName: string;
  uomName: string;
  status: string;
  /** The row as sent; null while it has errors. */
  value: SkuRowPayload | null;
  /** The row's cells as the file has them (trimmed), by property. */
  cells: Partial<Record<SkuPropKey, string>>;
  /** Field errors (JSON names); server row errors land here too. */
  errors: FieldError[];
  /** Soft warnings (packaging) — never block the import. */
  hints: string[];
}

export interface ParsedSkuCsv {
  /** Null when no header row with "SKU Code" was found. */
  rows: ParsedSkuRow[] | null;
  /** Sheet headers the file doesn't have (optional columns are fine). */
  missingRequired: string[];
  /** Columns of the file that are not SKU properties (ignored). */
  ignored: string[];
}

/**
 * Read ops' sheet export as-is: blank padding rows / columns are skipped, the
 * header is the first row with a "SKU Code" cell, headers match ignoring
 * case and spaces, Yes/No → booleans, Active/Non Active → status.
 */
export const parseSkuCsv = (textIn: string): ParsedSkuCsv => {
  const lines = parseCsvLines(textIn);
  const rows = lines.map((l) => l.cells);
  const headerAt = rows.findIndex((r) =>
    r.some((c) => norm(c) === 'skucode' || norm(c) === 'sku')
  );
  if (headerAt < 0) return { rows: null, missingRequired: [], ignored: [] };

  const header = rows[headerAt];
  const colOf = new Map<SkuPropKey, number>();
  const ignored: string[] = [];
  header.forEach((h, i) => {
    const key = HEADER_KEY.get(norm(h));
    if (key && !colOf.has(key)) colOf.set(key, i);
    else if (h.trim()) ignored.push(h.trim());
  });
  const missingRequired = SKU_PROPS.filter(
    (p) => p.required && !colOf.has(p.key)
  ).map((p) => p.label);

  const parsed = rows.slice(headerAt + 1).map((cells, idx) => {
    const cell = (k: SkuPropKey) => {
      const i = colOf.get(k);
      return i === undefined ? '' : (cells[i] ?? '').trim();
    };
    const draft = emptySkuDraft();
    const errors: FieldError[] = [];
    const byProp: Partial<Record<SkuPropKey, string>> = {};
    SKU_PROPS.forEach((p) => {
      const raw = cell(p.key);
      byProp[p.key] = raw;
      if (p.kind === 'bool') {
        const v = raw.toLowerCase();
        if (YES.has(v)) draft[p.key as SkuBoolKey] = true;
        else if (NO.has(v)) draft[p.key as SkuBoolKey] = false;
        else
          errors.push({
            field: p.key,
            message: `${p.label} harus Yes atau No (isi: "${raw}").`,
          });
      } else if (p.kind === 'status') {
        const v = raw.toLowerCase().replace(/\s+/g, '');
        if (ACTIVE.has(v)) draft.status = 'ACTIVE';
        else if (INACTIVE.has(v)) draft.status = 'INACTIVE';
        else
          errors.push({
            field: p.key,
            message: `Status harus Active atau Non Active (isi: "${raw}").`,
          });
      } else if (p.kind === 'uom') {
        draft.uomName = raw;
      } else {
        (draft as Record<string, unknown>)[p.key] = raw;
      }
    });
    const read = readSkuDraft(draft);
    const all = [...errors, ...read.errors];
    return {
      line: lines[headerAt + 1 + idx].line,
      sku: draft.sku.trim().toUpperCase(),
      itemName: draft.itemName.trim(),
      uomName: draft.uomName.trim().toLowerCase(),
      status: draft.status,
      value: all.length ? null : read.value,
      cells: byProp,
      errors: all,
      hints: packagingHints(draft),
    };
  });
  return { rows: parsed, missingRequired, ignored };
};

/** A field error's label for messages ("rows[2].weightKg" → "Weight / PCS (kg)"). */
export const skuPropLabel = (field: string): string => {
  const key = field.replace(/^rows?\[\d+\]\.?/, '') as SkuPropKey;
  return SKU_PROP[key]?.label ?? field;
};

/**
 * Every field error of the parsed rows (local and server), one per problem,
 * for the shared error table: the CSV row, the sheet column the field lives
 * in (its header), the cell as the file has it, and why.
 */
export const skuRowErrors = (rows: readonly ParsedSkuRow[]): CsvImportError[] =>
  rows.flatMap((r) =>
    r.errors.map((e) => {
      const key = e.field.replace(/^rows?\[\d+\]\.?/, '') as SkuPropKey;
      return {
        row: r.line,
        field: skuPropLabel(e.field),
        value: r.cells[key] ?? '',
        message: e.message,
      };
    })
  );
